/* ==========================================================================
   Flooring Attic — Desktop Mega Menu JS
   --------------------------------------------------------------------------
   Three responsibilities:
     1. Shop Products mega menu — fetch product counts for material links
        and mark the View All link with .fa-mega-viewall (so the red bar
        + arrow styling kicks in)
     2. Sale & Closeouts mega menu — clone the <template id="fa-sale-mega-
        template"> rendered by snippets/fa-sale-mega-menu.liquid into the
        nav row and hook up hover show/hide
     3. Best Sellers panel cards — swatch truncation (3 + "+N" chip) and
        the countdown ticker, because inline <script> tags inside the menu
        markup don't execute on some page types and the page-level collapse
        scripts are scoped to their own sections

   Idempotent: safe to load multiple times. Re-runs on DOM mutations.
   ========================================================================== */
(function () {
  'use strict';

  // ──────────────────────────────────────────────────────────────────
  // Configuration
  // ──────────────────────────────────────────────────────────────────
  var COUNTS_CACHE_KEY = '__fa_mega_collection_counts';
  var COUNTS_CACHE_TTL = 1000 * 60 * 60; // 1 hour
  var VIEW_ALL_PATTERNS = ['view all', 'shop all', 'all materials'];
  var SALE_LINK_HREF_MATCH = '/collections/sale-closeouts';
  var HOVER_HIDE_DELAY = 100;

  // ──────────────────────────────────────────────────────────────────
  // SECTION 1 — Shop Products: material counts + View All marker
  // ──────────────────────────────────────────────────────────────────

  function getCachedCounts() {
    try {
      var raw = sessionStorage.getItem(COUNTS_CACHE_KEY);
      if (!raw) return {};
      var parsed = JSON.parse(raw);
      if (parsed && parsed._timestamp && (Date.now() - parsed._timestamp) < COUNTS_CACHE_TTL) {
        return parsed.data || {};
      }
      return {};
    } catch (e) { return {}; }
  }

  function setCachedCounts(counts) {
    try {
      sessionStorage.setItem(COUNTS_CACHE_KEY, JSON.stringify({
        _timestamp: Date.now(),
        data: counts
      }));
    } catch (e) { /* sessionStorage may be disabled */ }
  }

  function fetchCollectionCount(handle) {
    return fetch('/collections/' + handle + '.json')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (!d || !d.collection) return null;
        return typeof d.collection.products_count === 'number'
          ? d.collection.products_count
          : null;
      })
      .catch(function () { return null; });
  }

  function getMaterialLinks() {
    return [].slice.call(document.querySelectorAll(
      'header-menu .mega-menu__column > div > a.mega-menu__link:not(.mega-menu__link--parent)'
    ));
  }

  function getCollectionHandle(link) {
    var href = link.getAttribute('href') || '';
    var m = href.match(/\/collections\/([^\/?#]+)/);
    return m ? m[1] : null;
  }

  function markViewAllLink(link) {
    var titleEl = link.querySelector('.mega-menu__link-title');
    var text = ((titleEl && titleEl.textContent) || link.textContent || '').trim().toLowerCase();
    for (var i = 0; i < VIEW_ALL_PATTERNS.length; i++) {
      if (text.indexOf(VIEW_ALL_PATTERNS[i]) !== -1) {
        link.classList.add('fa-mega-viewall');
        return;
      }
    }
  }

  function ensureCountSpan(link) {
    var span = link.querySelector('.mega-menu__count');
    if (!span) {
      span = document.createElement('span');
      span.className = 'mega-menu__count';
      link.appendChild(span);
    }
    return span;
  }

  function setCount(link, count) {
    if (link.classList.contains('fa-mega-viewall')) return;
    if (count === null || count === undefined) return;
    var span = ensureCountSpan(link);
    span.textContent = count + ' product' + (count === 1 ? '' : 's');
  }

  function processMaterialLinks() {
    var links = getMaterialLinks();
    if (!links.length) return;

    var cache = getCachedCounts();
    var newCache = {};
    var cacheChanged = false;
    for (var k in cache) { if (Object.prototype.hasOwnProperty.call(cache, k)) newCache[k] = cache[k]; }

    var pending = [];
    for (var i = 0; i < links.length; i++) {
      (function (link) {
        markViewAllLink(link);
        if (link.classList.contains('fa-mega-viewall')) return;

        var handle = getCollectionHandle(link);
        if (!handle) return;

        if (handle in cache) {
          setCount(link, cache[handle]);
          return;
        }

        pending.push(
          fetchCollectionCount(handle).then(function (count) {
            if (count !== null) {
              newCache[handle] = count;
              cacheChanged = true;
              setCount(link, count);
            }
          })
        );
      })(links[i]);
    }

    if (pending.length) {
      Promise.all(pending).then(function () {
        if (cacheChanged) setCachedCounts(newCache);
      });
    }
  }

  // Public API: refresh the count for a specific collection handle, or
  // clear the entire cache (pass nothing). Useful after admin updates.
  window.FaMegaMenuRefreshCounts = function (handle) {
    if (handle) {
      var cache = getCachedCounts();
      delete cache[handle];
      setCachedCounts(cache);
    } else {
      try { sessionStorage.removeItem(COUNTS_CACHE_KEY); } catch (e) {}
    }
    processMaterialLinks();
  };

  // ──────────────────────────────────────────────────────────────────
  // SECTION 2 — Sale & Closeouts dropdown injection
  // ──────────────────────────────────────────────────────────────────

  function setupSaleDropdown() {
    var saleLink = document.querySelector('header-menu a[href*="' + SALE_LINK_HREF_MATCH + '"]');
    if (!saleLink) return;

    var saleLi = saleLink.closest('li.menu-list__list-item');
    if (!saleLi) return;

    var template = document.getElementById('fa-sale-mega-template');
    if (!template) return;

    var navRow = saleLi.closest('.header__row');
    if (!navRow) return;

    // Already injected? bail
    if (navRow.querySelector('.fa-sale-mega-submenu')) return;

    // Anchor the submenu's absolute positioning to the full nav row
    if (getComputedStyle(navRow).position === 'static') {
      navRow.style.position = 'relative';
    }

    // Clone the template content into the nav row
    var fragment = template.content.cloneNode(true);
    navRow.appendChild(fragment);
    var submenu = navRow.querySelector('.fa-sale-mega-submenu');
    if (!submenu) return;

    // Hover handlers with a grace period so users can move from
    // the link onto the submenu without it closing
    var hideTimer;
    function show() {
      clearTimeout(hideTimer);
      submenu.classList.add('is-open');
    }
    function hide() {
      clearTimeout(hideTimer);
      hideTimer = setTimeout(function () {
        submenu.classList.remove('is-open');
      }, HOVER_HIDE_DELAY);
    }

    saleLi.addEventListener('mouseenter', show);
    saleLi.addEventListener('mouseleave', hide);
    submenu.addEventListener('mouseenter', show);
    submenu.addEventListener('mouseleave', hide);

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') submenu.classList.remove('is-open');
    });
  }

  // ──────────────────────────────────────────────────────────────────
  // SECTION 3 — Best Sellers panel cards: swatch truncation + countdown
  //
  // Inline <script> tags inside the menu markup don't execute on some
  // page types, and the site's page-level swatch collapse scripts are
  // scoped to their own sections (event panels, collection grids) so
  // they never reach the header. This external file always runs, so the
  // menu maintains its own cards: collapse each swatch list to 3 + a
  // "+N" chip (.fa-hcard__swmore, styled in fa-product-card-
  // horizontal.css) and start the shared countdown ticker when no page
  // section already did. Scoped to .fa-mega-best-list — touches no
  // other cards anywhere.
  // ──────────────────────────────────────────────────────────────────

  function collapseMenuSwatches() {
    var lists = document.querySelectorAll('.fa-mega-best-list .fa-rec-swatch-list, .fa-sale-mega-drop-list .fa-rec-swatch-list');
    for (var i = 0; i < lists.length; i++) {
      var list = lists[i];
      try {
        if (list.dataset.faMegaSw) continue;
        var kids = [].slice.call(list.children);
        /* Drop any leftover chip from an earlier script version */
        kids.forEach(function (k) {
          var isChip = (k.textContent || '').trim().charAt(0) === '+';
          if (isChip && !(k.classList && k.classList.contains('fa-hcard__swmore'))) k.remove();
        });
        var sw = kids.filter(function (k) {
          if (!k.classList || !k.classList.contains('fa-rec-swatch')) return false;
          if (k.classList.contains('fa-hcard__swmore')) return false;
          return (k.textContent || '').trim().charAt(0) !== '+';
        });
        if (sw.length > 3) {
          var extras = [];
          for (var j = 3; j < sw.length; j++) {
            var eImg = sw[j].querySelector('img');
            var eName = (sw[j].getAttribute('title') || sw[j].getAttribute('data-color') || '').trim();
            if (eImg && eName) extras.push({ src: eImg.getAttribute('src'), name: eName });
            /* inline !important — a theme card stylesheet re-shows swatches
               with an !important display rule, which silently defeated a
               plain style.display hide (swatch-truncate hides this same
               way for the same reason) */
            sw[j].style.setProperty('display', 'none', 'important');
          }
          var more = document.createElement('span');
          /* fa-swatch-more = the site's own chip class (fa-swatch-condence.css)
             so this chip is styled EXACTLY like the vertical cards' button.
             Pure styling reuse: their script only binds to chips it creates,
             and its swatch filters exclude this class. */
          more.className = 'fa-rec-swatch fa-hcard__swmore fa-swatch-more';
          more.textContent = '+' + (sw.length - 3);
          more.__faExtras = extras;
          more.style.cursor = 'pointer';
          more.setAttribute('role', 'button');
          more.setAttribute('tabindex', '0');
          more.setAttribute('aria-label', 'Show ' + extras.length + ' more colors');
          /* clicks are handled by document-level capture delegation below —
             per-chip listeners die when the theme re-renders the header */
          list.appendChild(more);
        }
        list.dataset.faMegaSw = '1';
      } catch (err) {
        /* Never let one bad list break the rest of the menu */
      }
    }
  }

  // "+N" chip popover — self-contained centered modal, visually identical
  // to the site's swatch popup (values mirrored in fa-mega-menu.css).
  var SWPOP_ID = 'fa-mega-swpop';

  function closeSwatchPopover() {
    var p = document.getElementById(SWPOP_ID);
    if (p) p.remove();
  }

  function onChipClick(e) {
    e.preventDefault();
    e.stopImmediatePropagation();
    var existing = document.getElementById(SWPOP_ID);
    var wasOwn = existing && existing.__faChip === this;
    closeSwatchPopover();
    if (wasOwn) return; // second click on the same chip toggles closed
    openSwatchPopover(this);
  }

  function openSwatchPopover(chip) {
    var extras = chip.__faExtras || [];
    if (!extras.length) return;
    var card = chip.closest('.fa-hcard');
    var title = (card && card.getAttribute('data-compare-title')) || '';
    if (!title && card) {
      var titleNode = card.querySelector('.fa-hcard__ptitle, .fa-hcard__title');
      if (titleNode) title = (titleNode.textContent || '').trim();
    }
    var url = (card && card.getAttribute('data-compare-url')) || '';
    if (!url && card) {
      var linkNode = card.querySelector('.fa-hcard__titlelink, a.fa-hcard__media');
      if (linkNode) url = linkNode.getAttribute('href') || '';
    }

    var pop = document.createElement('div');
    pop.id = SWPOP_ID;
    pop.__faChip = chip;
    pop.addEventListener('click', function (e) {
      if (e.target === pop) closeSwatchPopover();
    });

    var inner = document.createElement('div');
    inner.className = 'fa-mega-swpop__inner';

    var titleWrap = document.createElement('div');
    titleWrap.className = 'fa-mega-swpop__titlewrap';
    var eyebrow = document.createElement('span');
    eyebrow.className = 'fa-mega-swpop__eyebrow';
    eyebrow.textContent = 'More Colors For';
    titleWrap.appendChild(eyebrow);
    if (title) {
      var titleEl = document.createElement('span');
      titleEl.className = 'fa-mega-swpop__title';
      titleEl.textContent = title;
      titleWrap.appendChild(titleEl);
    }
    inner.appendChild(titleWrap);

    var listEl = document.createElement('div');
    listEl.className = 'fa-mega-swpop__list';
    extras.forEach(function (x) {
      var row = document.createElement('div');
      row.className = 'fa-mega-swpop__row';
      var img = document.createElement('img');
      img.src = x.src;
      img.alt = '';
      var name = document.createElement('span');
      name.textContent = x.name;
      row.appendChild(img);
      row.appendChild(name);
      listEl.appendChild(row);
    });
    inner.appendChild(listEl);

    if (url) {
      var btn = document.createElement('a');
      btn.className = 'fa-mega-swpop__btn';
      btn.href = url;
      btn.textContent = 'View Product';
      inner.appendChild(btn);
    }

    pop.appendChild(inner);
    document.body.appendChild(pop);
  }

  var CHIP_SEL = '.fa-mega-best-list .fa-hcard__swmore, .fa-sale-mega-drop-list .fa-hcard__swmore';

  document.addEventListener('click', function (e) {
    var chip = e.target.closest && e.target.closest(CHIP_SEL);
    if (!chip) return;
    onChipClick.call(chip, e);
  }, true);

  document.addEventListener('pointerdown', function (e) {
    var chip = e.target.closest && e.target.closest(CHIP_SEL);
    if (chip) { e.stopPropagation(); return; }
    var p = document.getElementById(SWPOP_ID);
    if (!p) return;
    if (p.contains(e.target)) return;
    closeSwatchPopover();
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { closeSwatchPopover(); return; }
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var chip = e.target.closest && e.target.closest(CHIP_SEL);
    if (chip) onChipClick.call(chip, e);
  }, true);

  function ensureCountdownTicker() {
    if (window.__faCdShort) return;
    if (!document.querySelector('.fa-mega-best-list [data-fa-cd-short], .fa-sale-mega-drop-list [data-fa-cd-short]')) return;
    window.__faCdShort = true;
    function tick() {
      var els = document.querySelectorAll('[data-fa-cd-short]');
      if (!els.length) return;
      var now = Date.now();
      els.forEach(function (el) {
        var t = parseInt(el.getAttribute('data-fa-cd-short'), 10) * 1000;
        if (!t) return;
        var d = t - now;
        if (d <= 0) { el.textContent = 'Deal ended'; return; }
        var days = Math.floor(d / 86400000);
        var hrs = Math.floor((d % 86400000) / 3600000);
        var mins = Math.floor((d % 3600000) / 60000);
        el.textContent = 'Ends in ' + (days > 0 ? days + 'd ' : '') + hrs + 'h ' + mins + 'm';
      });
    }
    tick();
    setInterval(tick, 60000);
  }

  // ──────────────────────────────────────────────────────────────────
  // Init + observer (handles SPA / theme-editor re-renders)
  // ──────────────────────────────────────────────────────────────────

  function init() {
    try { processMaterialLinks(); } catch (e) {}
    try { setupSaleDropdown(); } catch (e) {}
    try { collapseMenuSwatches(); } catch (e) {}
    try { ensureCountdownTicker(); } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Single observer per page (idempotent across re-loads of this script)
  if (!window.__faMegaMenuObs) {
    var obs = new MutationObserver(function () {
      clearTimeout(window.__faMegaMenuObsTimer);
      window.__faMegaMenuObsTimer = setTimeout(init, 200);
    });
    obs.observe(document.body, { childList: true, subtree: true });
    window.__faMegaMenuObs = obs;
  }
})();